import { getFeatureDefinition } from '@gitbook/app-pricing';

import { useCurrentOrganization } from './organization';

/**
 * Access levels for the broken links feature.
 */
export enum BrokenLinksAccessLevel {
    /** All content */
    Full = 'full',
    /** Only for main branches and potentially a subset of the broken links */
    Preview = 'preview',
    /** No access */
    None = 'none',
}

/**
 * Hook to check if the current context of space or change request has access to the broken links feature.
 *
 * We support broken links for:
 *   - primary space content and change requests for pro+ orgs in `full` mode
 *   - primary space content for all orgs in `preview` mode
 */
export function useBrokenLinksFeatureAccess({
    isInChangeRequest,
}: {
    isInChangeRequest: boolean;
}): BrokenLinksAccessLevel {
    const organization = useCurrentOrganization();
    const feature = getFeatureDefinition(organization.plan, 'broken-links');
    const hasOrganizationFeature = !!feature?.[organization.plan];

    // main branch for an org without the feature
    if (!hasOrganizationFeature && !isInChangeRequest) {
        return BrokenLinksAccessLevel.Preview;
    }

    return hasOrganizationFeature ? BrokenLinksAccessLevel.Full : BrokenLinksAccessLevel.None;
}

/**
 * Hook to check if the content can show backlinks.
 *
 * We support backlinks for:
 *   - primary space content and change requests for pro+ orgs
 *   - primary space content for all orgs
 */
export function useSupportsBacklinks({ isInChangeRequest }: { isInChangeRequest: boolean }) {
    const organization = useCurrentOrganization();
    const feature = getFeatureDefinition(organization.plan, 'broken-links');
    const hasOrganizationFeature = !!feature?.[organization.plan];

    if (!isInChangeRequest) {
        return true;
    }

    return hasOrganizationFeature;
}
